import type { Chapter } from "@/mocks/chapters";
import type { Manga } from "@/mocks/manga";

interface MangaReaderProps {
  manga: Manga;
  chapterIdx: number;
  onChangeChapter: (next: number) => void;
  chapters: Chapter[];
}

const readerPages = [
  "https://readdy.ai/api/search-image?query=full%20color%20comic%20page%20panel%20of%20a%20teenage%20girl%20standing%20nervously%20at%20the%20edge%20of%20an%20outdoor%20swimming%20pool%20on%20a%20sunny%20day%2C%20speech%20bubbles%2C%20coral%20and%20teal%20color%20palette%2C%20manga%20art%20style%20with%20clean%20lines%2C%20high%20detail&width=900&height=1300&seq=page-overflow-01&orientation=portrait",
  "https://readdy.ai/api/search-image?query=full%20color%20comic%20page%20panel%20of%20a%20boy%20swimming%20lane%20and%20a%20girl%20practicing%20breathing%20at%20the%20pool%20edge%20during%20training%2C%20splashes%20and%20water%20reflections%2C%20speech%20bubbles%2C%20coral%20and%20teal%20tones%2C%20manga%20art%20style%2C%20high%20detail&width=900&height=1300&seq=page-overflow-02&orientation=portrait",
  "https://readdy.ai/api/search-image?query=full%20color%20comic%20page%20panel%20of%20two%20teenagers%20sitting%20on%20poolside%20tiles%20after%20practice%20talking%20with%20towels%20around%20their%20necks%2C%20golden%20afternoon%20light%2C%20speech%20bubbles%2C%20coral%20and%20teal%20color%20palette%2C%20manga%20art%20style%2C%20high%20detail&width=900&height=1300&seq=page-overflow-03&orientation=portrait",
  "https://readdy.ai/api/search-image?query=full%20color%20comic%20page%20panel%20of%20a%20boy%20handing%20a%20girl%20a%20matching%20swimming%20goggles%20by%20the%20pool%20with%20shy%20expressions%2C%20soft%20sparkles%20and%20bokeh%2C%20speech%20bubbles%2C%20coral%20and%20teal%20tones%2C%20manga%20art%20style%2C%20high%20detail&width=900&height=1300&seq=page-overflow-04&orientation=portrait",
  "https://readdy.ai/api/search-image?query=full%20color%20comic%20page%20panel%20of%20an%20evening%20night%20swimming%20pool%20scene%20with%20stars%20and%20soft%20blue%20water%20glow%2C%20two%20silhouettes%20sitting%20at%20the%20pool%20edge%20close%20together%2C%20speech%20bubbles%2C%20coral%20and%20teal%20tones%2C%20manga%20art%20style%2C%20high%20detail&width=900&height=1300&seq=page-overflow-05&orientation=portrait",
];

export default function MangaReader({ manga, chapterIdx, onChangeChapter, chapters }: MangaReaderProps) {
  const chapter = chapters[chapterIdx];

  const changeChapter = (next: number) => {
    if (next < 0 || next >= chapters.length) return;
    onChangeChapter(next);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
        <div>
          <h2 className="font-heading text-lg text-foreground-950">
            {manga.title} · 第{chapter.num}话 {chapter.title}
          </h2>
          <p className="mt-1 text-xs text-foreground-500">共 {chapters.length} 话 · 每周六更新 · 全屏阅读体验</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeChapter(chapterIdx - 1)}
            disabled={chapterIdx === 0}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-200 px-4 py-2 text-sm text-foreground-700 hover:bg-background-300 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-line"></i>上一话
          </button>
          <button
            onClick={() => changeChapter(chapterIdx + 1)}
            disabled={chapterIdx === chapters.length - 1}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一话<i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-col items-center gap-5">
        {readerPages.map((src, i) => (
          <div key={i} className="w-full max-w-2xl overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <img src={src} alt={`${manga.title} 第${chapter.num}话 阅读页 ${i + 1}`} title={`${manga.title} 漫画 免费阅读 第${chapter.num}话`} className="h-auto w-full object-contain" />
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between gap-2">
        <button
          onClick={() => changeChapter(chapterIdx - 1)}
          disabled={chapterIdx === 0}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-skip-back-line"></i>上一话
        </button>
        <span className="text-sm text-foreground-500">
          {chapterIdx + 1} / {chapters.length}
        </span>
        <button
          onClick={() => changeChapter(chapterIdx + 1)}
          disabled={chapterIdx === chapters.length - 1}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          下一话<i className="ri-skip-forward-line"></i>
        </button>
      </div>
    </div>
  );
}